import { useEffect, useRef, useState } from 'react';
import { File, FileText, Loader2, Music, Video } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Thumbnails are written to S3 by the thumbnail-generator Lambda a few seconds
 * after the original lands, so a file rendered straight after upload has a
 * signed thumbnail URL that 404s. This component keeps a fresh upload's
 * thumbnail request alive with a short backoff, then degrades: original image
 * (images only) → file-type icon. Older files skip the retries.
 */

/** Uploads younger than this keep polling for their thumbnail. */
const FRESH_WINDOW_MS = 3 * 60 * 1000;
/** Backoff between retries; ~22s total, comfortably past a Lambda cold start. */
const RETRY_DELAYS_MS = [1500, 3000, 6000, 12000];

function fileTypeIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType === 'application/pdf' || mimeType.includes('document') || mimeType.startsWith('text/')) return FileText;
  return File;
}

interface AttachmentThumbnailProps {
  thumbnailUrl?: string | null;
  originalUrl?: string | null;
  mimeType: string;
  fileName: string;
  /** ISO timestamp. Recent uploads retry the thumbnail while the Lambda catches up. */
  createdAt?: string;
  /** Re-fetch signed URLs before a retry; a new signature also sidesteps any cached error. */
  onRefresh?: () => Promise<unknown> | void;
  /** Wrapper classes (sizing, border, rounding). */
  className?: string;
  /** Classes for the <img> itself. */
  imgClassName?: string;
}

type Mode = 'thumbnail' | 'original' | 'icon';

export function AttachmentThumbnail({
  thumbnailUrl,
  originalUrl,
  mimeType,
  fileName,
  createdAt,
  onRefresh,
  className,
  imgClassName,
}: AttachmentThumbnailProps) {
  const [mode, setMode] = useState<Mode>('thumbnail');
  const [attempt, setAttempt] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const isImage = mimeType.startsWith('image/');
  const isFresh = createdAt ? Date.now() - new Date(createdAt).getTime() < FRESH_WINDOW_MS : false;

  // Resolve what to show for the current mode, skipping URLs we don't have.
  let src: string | null = null;
  if (mode === 'thumbnail' && thumbnailUrl) src = thumbnailUrl;
  else if (mode !== 'icon' && isImage && originalUrl) src = originalUrl;

  const handleError = () => {
    setLoaded(false);
    if (mode === 'thumbnail') {
      if (isFresh && attempt < RETRY_DELAYS_MS.length) {
        setWaiting(true);
        timer.current = window.setTimeout(async () => {
          try {
            await onRefresh?.();
          } catch {
            // Retry the URL we already have.
          }
          setAttempt((a) => a + 1);
          setWaiting(false);
        }, RETRY_DELAYS_MS[attempt]);
        return;
      }
      if (isImage && originalUrl) {
        setMode('original');
        return;
      }
    }
    setMode('icon');
  };

  const spinner = (
    <div className="absolute inset-0 flex items-center justify-center" aria-label="Loading thumbnail">
      <Loader2 className="h-6 w-6 animate-spin text-grey-400" />
    </div>
  );

  if (waiting) {
    return (
      <div className={cn('relative bg-grey-100', className)} title={fileName}>
        {spinner}
      </div>
    );
  }

  if (!src) {
    const Icon = fileTypeIcon(mimeType);
    return (
      <div
        className={cn('flex min-h-[5rem] min-w-[5rem] items-center justify-center bg-grey-100 text-grey-400', className)}
        title={fileName}
      >
        <Icon className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className={cn('relative bg-grey-100', className)}>
      {!loaded && spinner}
      <img
        key={`${mode}-${attempt}`}
        src={src}
        alt={fileName}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={handleError}
        className={cn(
          'transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
          imgClassName,
        )}
      />
    </div>
  );
}
